/**
 * Generate's clear-case image as the clear-phone (`behind`) layer. The image shares the case
 * image's frame, and the engine cover-fits `behind` onto the bounding box of the product mask plus
 * the print mask, so the layer is the frame-aligned image cropped to exactly that box: the
 * cover-fit is then an identity placement and the clear case sits 1:1 under the keyed print area.
 */
import { decodePngToRgba, rgbaToPngBytes } from "./pixels.js";

export interface Box { x: number; y: number; w: number; h: number }

/** The bounding box of `mask` ∪ `extra` in pixels, or null when both are empty. */
export function maskBox(mask: Uint8Array, w: number, h: number, extra?: Uint8Array): Box | null {
  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (mask[y * w + x] === 0 && !extra?.[y * w + x]) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  return maxX < 0 ? null : { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/** The `box` region of a `w`-wide RGBA frame. */
export function cropRgba(rgba: Uint8ClampedArray, w: number, box: Box): Uint8ClampedArray {
  const out = new Uint8ClampedArray(box.w * box.h * 4);
  for (let row = 0; row < box.h; row++) {
    const start = ((box.y + row) * w + box.x) * 4;
    out.set(rgba.subarray(start, start + box.w * 4), row * box.w * 4);
  }
  return out;
}

// Live previews re-compose on every slider move; the crop only changes with the image or the box.
const crops = new WeakMap<Uint8Array, { key: string; png: Uint8Array }>();

/** The clear image, drawn into the `w`×`h` case frame and cropped to the box the engine places
 *  `behind` on, as PNG. */
export async function clearLayerForFrame(
  image: Uint8Array, productMask: Uint8Array, w: number, h: number, printMask?: Uint8Array,
): Promise<Uint8Array | null> {
  const box = maskBox(productMask, w, h, printMask);
  if (!box) return null;
  const key = `${w}x${h}:${box.x},${box.y},${box.w},${box.h}`;
  const cached = crops.get(image);
  if (cached?.key === key) return cached.png;
  const frame = await decodePngToRgba(image, w, h);
  const png = frame ? rgbaToPngBytes(cropRgba(frame, w, box), box.w, box.h) : null;
  if (png) crops.set(image, { key, png });
  return png;
}
